
// ── Constants ──

const EARLIEST_CLAIMING_AGE = 62;
const LATEST_CLAIMING_AGE = 70;

// Worker early reduction rates
const EARLY_REDUCTION_RATE_FIRST_36 = 5 / 900; // per month (~6.67%/yr)
const EARLY_REDUCTION_RATE_AFTER_36 = 5 / 1200; // per month (~5%/yr)


// Delayed retirement credits: 8% per year after FRA (for birth year 1943+)
const DELAYED_CREDIT_ANNUAL = 0.08;

// ── Full Retirement Age by Birth Year ──

export function getFullRetirementAge(birthYear) {
  if (!birthYear || birthYear >= 1960) return 67;
  if (birthYear <= 1937) return 65;
  if (birthYear <= 1954) return 66;
  // 1955-1959: 66 + 2 months per year
  const monthsOver66 = (birthYear - 1954) * 2;
  return 66 + monthsOver66 / 12;
}

// ── Benefit Adjustment by Claiming Age ──

export function adjustBenefitForClaimingAge(benefitAtFRA, claimingAge, birthYear) {
  if (claimingAge < EARLIEST_CLAIMING_AGE) return 0;
  if (claimingAge > LATEST_CLAIMING_AGE) claimingAge = LATEST_CLAIMING_AGE;

  const fra = getFullRetirementAge(birthYear);
  const monthsFromFRA = Math.round((claimingAge - fra) * 12);

  if (monthsFromFRA < 0) {
    const earlyMonths = Math.abs(monthsFromFRA);
    const first36 = Math.min(earlyMonths, 36);
    const beyond36 = Math.max(0, earlyMonths - 36);
    const reduction = first36 * EARLY_REDUCTION_RATE_FIRST_36 + beyond36 * EARLY_REDUCTION_RATE_AFTER_36;
    return benefitAtFRA * (1 - reduction);
  }

  if (monthsFromFRA > 0) {
    const delayedYears = monthsFromFRA / 12;
    return benefitAtFRA * (1 + delayedYears * DELAYED_CREDIT_ANNUAL);
  }

  return benefitAtFRA;
}

// ── Spousal and survivor benefits ──

// A spousal benefit claimed early is cut by 25/36 of 1% a month for the first
// 36 months and 5/12 of 1% a month beyond. It earns no delayed credits.
const SPOUSAL_REDUCTION_FIRST_36 = 25 / 3600;
const SPOUSAL_REDUCTION_AFTER_36 = 5 / 1200;
export const SPOUSAL_SHARE = 0.5;
// Survivor benefits start at 60 at the earliest, at 71.5% of the full amount.
export const SURVIVOR_EARLIEST_AGE = 60;
const SURVIVOR_FLOOR = 0.715;
// A survivor of someone who claimed early still gets at least this share of their full benefit.
export const WIDOW_LIMIT = 0.825;

/** Share of the full spousal amount paid when it is claimed at `claimingAge`. */
export function spousalFactor(claimingAge, birthYear) {
  const earlyMonths = Math.max(0, Math.round((getFullRetirementAge(birthYear) - claimingAge) * 12));
  const first36 = Math.min(earlyMonths, 36);
  return Math.max(0, 1 - first36 * SPOUSAL_REDUCTION_FIRST_36 - Math.max(0, earlyMonths - 36) * SPOUSAL_REDUCTION_AFTER_36);
}

/** Monthly excess spousal benefit: half the other's full benefit less one's own, adjusted for claiming age. */
export function spousalBenefit(ownAtFRA, otherAtFRA, claimingAge, birthYear) {
  const excess = Math.max(0, SPOUSAL_SHARE * (otherAtFRA || 0) - (ownAtFRA || 0));
  return excess * spousalFactor(claimingAge, birthYear);
}

/** Share of the survivor benefit paid when it starts at `startAge`: 71.5% at 60 rising evenly to 100% at full retirement age. */
export function survivorFactor(startAge, birthYear) {
  const fra = getFullRetirementAge(birthYear);
  if (startAge >= fra) return 1;
  if (startAge < SURVIVOR_EARLIEST_AGE) return 0;
  return SURVIVOR_FLOOR + (1 - SURVIVOR_FLOOR) * (startAge - SURVIVOR_EARLIEST_AGE) / (fra - SURVIVOR_EARLIEST_AGE);
}

/**
 * Monthly survivor benefit before the survivor's own early reduction. If the
 * person who died had claimed, it is what they were paid, but at least 82.5% of
 * their full benefit; if not, their full benefit plus any delayed credits
 * earned up to the age they died (no early reduction).
 */
export function survivorBase(deceasedAtFRA, deceasedClaimAge, deathAge, deceasedBirthYear) {
  if (!(deceasedAtFRA > 0)) return 0;
  if (deathAge >= deceasedClaimAge) {
    return Math.max(adjustBenefitForClaimingAge(deceasedAtFRA, deceasedClaimAge, deceasedBirthYear), WIDOW_LIMIT * deceasedAtFRA);
  }
  const fra = getFullRetirementAge(deceasedBirthYear);
  return deathAge > fra ? adjustBenefitForClaimingAge(deceasedAtFRA, deathAge, deceasedBirthYear) : deceasedAtFRA;
}

// ── Estimating a benefit from earnings ──

// The 2026 benefit formula: the monthly amounts at which the replacement rate
// steps down, and the most earnings counted in a year.
export const PIA_BEND_POINTS = [1286, 7749];
export const PIA_RATES = [0.90, 0.32, 0.15];
export const SS_WAGE_BASE = 184500;
export const SS_YEARS_COUNTED = 35;

/**
 * Monthly benefit at full retirement age from a list of yearly earnings in
 * today's dollars. The highest 35 years are averaged (missing years count as
 * zero), each capped at the taxable maximum, and run through the formula.
 */
export function estimateMonthlyBenefit(earnings) {
  const counted = (earnings || [])
    .map((amount) => Math.min(SS_WAGE_BASE, Math.max(0, Number(amount) || 0)))
    .sort((a, b) => b - a)
    .slice(0, SS_YEARS_COUNTED);
  const averageMonthly = counted.reduce((sum, amount) => sum + amount, 0) / (SS_YEARS_COUNTED * 12);
  const [first, second] = PIA_BEND_POINTS;
  return PIA_RATES[0] * Math.min(averageMonthly, first)
    + PIA_RATES[1] * Math.min(Math.max(0, averageMonthly - first), second - first)
    + PIA_RATES[2] * Math.max(0, averageMonthly - second);
}
